/**
 * BMP: instant quantity updates in the cart drawer.
 *
 * The stock flow waits for the /cart/change request (plus a debounce) before
 * the line price and checkout total change, and shimmers them meanwhile. This
 * module updates those numbers in the browser the moment +/- is pressed, from
 * per-line data rendered by snippets/cart-products.liquid:
 *   data-bmp-line-cents  line price after all discounts (incl. code share)
 *   data-bmp-was-cents   crossed-out "was" line price
 *   data-bmp-qty         quantity the server rendered
 * The server response still morphs in moments later with the exact values
 * (and the free shipping bar / discount rows), replacing these estimates.
 * If the update fails (e.g. not enough stock), the numbers are reverted.
 */
import { formatMoney } from '@theme/money-formatting';

const QTY_EVENT = 'quantity-selector:update';
const ERROR_EVENT = 'shopify:cart:error';

/** @returns {HTMLElement | null} */
const getDrawer = () => document.getElementById('cart-drawer');

/**
 * @param {HTMLElement} drawer
 * @returns {((cents: number) => string) | null}
 */
function getFormatter(drawer) {
  const holder = /** @type {HTMLElement | null} */ (drawer.querySelector('[data-bmp-money-format]'));
  const format = holder?.dataset.bmpMoneyFormat;
  const currency = holder?.dataset.bmpCurrency;
  if (!format || !currency) return null;
  return (cents) => formatMoney(Math.max(0, Math.round(cents)), format, currency);
}

/**
 * Writes a money value into a text-component (text + shimmer value) or plain element.
 * @param {Element | null} el
 * @param {string} text
 */
function setMoney(el, text) {
  if (!el) return;
  el.textContent = text;
  if (el.tagName === 'TEXT-COMPONENT') {
    el.setAttribute('value', text);
    el.setAttribute('data-bmp-instant', '');
  }
}

/**
 * Applies a quantity to one line's price cell and adjusts the drawer totals.
 * @param {HTMLElement} drawer
 * @param {HTMLElement} cell - The [data-bmp-line-cents] price cell.
 * @param {number} quantity
 */
function applyQuantity(drawer, cell, quantity) {
  const fmt = getFormatter(drawer);
  if (!fmt) return;

  const baseQty = Number(cell.dataset.bmpQty);
  const baseFinal = Number(cell.dataset.bmpLineCents);
  const baseWas = Number(cell.dataset.bmpWasCents);
  if (!baseQty || Number.isNaN(baseFinal) || Number.isNaN(baseWas)) return;

  const prevQty = Number(cell.dataset.bmpCurrentQty || baseQty);
  if (quantity === prevQty) return;

  const line = (/** @type {number} */ base, /** @type {number} */ qty) => Math.round((base / baseQty) * qty);
  const prevFinal = line(baseFinal, prevQty);
  const prevWas = line(baseWas, prevQty);
  const nextFinal = line(baseFinal, quantity);
  const nextWas = line(baseWas, quantity);
  cell.dataset.bmpCurrentQty = String(quantity);

  // Line price, crossed-out price, and "(Save $X)".
  setMoney(cell.querySelector(':scope > text-component'), fmt(nextFinal));
  const wasEl = cell.querySelector('.bmp-line-savings__was');
  if (wasEl) wasEl.textContent = fmt(nextWas);
  const saveEl = cell.querySelector('.bmp-line-savings');
  if (saveEl && saveEl.textContent) {
    saveEl.textContent = saveEl.textContent.replace(fmt(prevWas - prevFinal), fmt(nextWas - nextFinal));
  }

  // Checkout button total.
  const totalEl = /** @type {HTMLElement | null} */ (drawer.querySelector('[data-bmp-total-cents]'));
  if (totalEl) {
    const current = Number(totalEl.dataset.bmpCurrentCents ?? totalEl.dataset.bmpTotalCents);
    const next = current + (nextFinal - prevFinal);
    totalEl.dataset.bmpCurrentCents = String(next);
    setMoney(totalEl, fmt(next));
  }

  // "You're saving" row.
  const savingsEl = /** @type {HTMLElement | null} */ (drawer.querySelector('[data-bmp-savings-cents]'));
  if (savingsEl) {
    const current = Number(savingsEl.dataset.bmpCurrentCents ?? savingsEl.dataset.bmpSavingsCents);
    const next = current + (nextWas - nextFinal) - (prevWas - prevFinal);
    savingsEl.dataset.bmpCurrentCents = String(next);
    savingsEl.textContent = fmt(next);
  }
}


/**
 * Optimistic free-shipping progress. The Shopify section response still
 * supplies the authoritative amount after discounts and gift recalculations.
 * @param {HTMLElement} drawer
 * @param {HTMLElement} cell
 * @param {number} quantity
 */
function previewShipping(drawer, cell, quantity) {
  const bar = drawer.querySelector('.bmp-free-shipping-bar[data-bmp-fsb-goal]');
  if (!(bar instanceof HTMLElement)) return;
  const baseQty = Number(cell.dataset.bmpQty);
  const previousQty = Number(cell.dataset.bmpCurrentQty ?? baseQty);
  const baseFinal = Number(cell.dataset.bmpLineCents);
  if (!baseQty || !Number.isFinite(baseFinal) || !Number.isFinite(previousQty)) return;
  const deltaQty = quantity - previousQty;
  const deltaCents = Math.round((baseFinal / baseQty) * quantity) - Math.round((baseFinal / baseQty) * previousQty);
  const type = bar.dataset.bmpFsbGoalType;
  const difference = type === 'quantity' ? deltaQty : deltaCents;
  const current = Number(bar.dataset.bmpFsbPreviewCurrent ?? bar.dataset.bmpFsbCurrent);
  if (!Number.isFinite(current) || !Number.isFinite(difference)) return;
  const next = Math.max(0, current + difference);
  bar.dataset.bmpFsbPreviewCurrent = String(next);
  showShippingProgress(drawer, bar, next);
}

/** @param {HTMLElement} drawer @param {HTMLElement} bar @param {number} current */
function showShippingProgress(drawer, bar, current) {
  const goal = Number(bar.dataset.bmpFsbGoal);
  if (!(goal > 0)) return;
  const remaining = goal - current;
  const percent = Math.max(0, Math.min(100, Math.round((current / goal) * 100)));
  const track = bar.querySelector('.bmp-free-shipping-bar__track');
  if (track instanceof HTMLElement) {
    track.style.setProperty('--bmp-fsb-progress', percent + '%');
    track.setAttribute('aria-valuenow', String(percent));
  }
  bar.classList.toggle('bmp-free-shipping-bar--complete', remaining <= 0);
  const message = bar.querySelector('.bmp-free-shipping-bar__message');
  if (!(message instanceof HTMLElement)) return;
  if (remaining <= 0) {
    message.textContent = bar.dataset.bmpFsbSuccess || '';
    return;
  }
  const amount = bar.dataset.bmpFsbGoalType === 'quantity'
    ? String(remaining)
    : getFormatter(drawer)?.(remaining);
  if (amount === undefined) return;
  // Build text nodes instead of interpreting merchant theme settings as HTML.
  const template = (bar.dataset.bmpFsbMessage || '').replaceAll('$[amount]', '[amount]');
  const segments = template.split('[amount]');
  const nodes = [];
  segments.forEach((segment, index) => {
    if (index > 0) {
      const strong = document.createElement('strong');
      strong.className = 'bmp-free-shipping-bar__amount';
      strong.textContent = amount;
      nodes.push(strong);
    }
    if (segment) nodes.push(document.createTextNode(segment));
  });
  message.replaceChildren(...nodes);
}

/** @param {HTMLElement} drawer */
function resetShippingPreview(drawer) {
  const bar = drawer.querySelector('.bmp-free-shipping-bar[data-bmp-fsb-goal]');
  if (!(bar instanceof HTMLElement)) return;
  delete bar.dataset.bmpFsbPreviewCurrent;
  const initial = Number(bar.dataset.bmpFsbCurrent);
  if (Number.isFinite(initial)) showShippingProgress(drawer, bar, initial);
}
document.addEventListener(
  QTY_EVENT,
  (event) => {
    const drawer = getDrawer();
    const target = event.target;
    if (!drawer || !(target instanceof Element) || !drawer.contains(target)) return;

    const detail = /** @type {{ quantity?: number, cartLine?: number } | undefined} */ (
      /** @type {any} */ (event).detail
    );
    const quantity = Number(detail?.quantity);
    if (!detail?.cartLine || !Number.isFinite(quantity) || quantity < 0) return;

    const row = target.closest('.cart-items__table-row');
    const cell = /** @type {HTMLElement | null} */ (row?.querySelector('[data-bmp-line-cents]') ?? null);
    if (!cell) return;

    try {
      previewShipping(drawer, cell, quantity);
      if (quantity > 0) applyQuantity(drawer, cell, quantity);
    } catch (error) {
      console.warn('[bmp-cart-instant-quantity]', error);
    }
  },
  true
);

// Failed update (e.g. not enough stock): put every touched line back to the
// quantity the server last rendered.
document.addEventListener(
  ERROR_EVENT,
  () => {
    const drawer = getDrawer();
    if (!drawer) return;
    resetShippingPreview(drawer);
    drawer.querySelectorAll('[data-bmp-line-cents][data-bmp-current-qty]').forEach((cell) => {
      if (!(cell instanceof HTMLElement)) return;
      try {
        applyQuantity(drawer, cell, Number(cell.dataset.bmpQty));
      } catch (error) {
        console.warn('[bmp-cart-instant-quantity]', error);
      }
    });
  },
  true
);
